import type { FullParseResultResponse } from "@/api/docParsing.service";
import { type LegacyParsedJson, narrowLegacyParsedJson } from "./originalSubmissionSections";

export type OriginalSubmissionLoad =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; status: number | null; code: string | null }
  | { kind: "loaded"; result: FullParseResultResponse };

export type OriginalSubmissionState =
  | { kind: "loading" }
  | { kind: "notAvailable" }
  | { kind: "error" }
  | { kind: "failed"; document: SubmissionDocument | null }
  | { kind: "notFinished"; document: SubmissionDocument | null }
  | { kind: "sectionsLegacy"; result: FullParseResultResponse; parsedJson: LegacyParsedJson };

/** The parse result whose document can be downloaded, and the name to save it under. */
export type SubmissionDocument = { docParseResultId: string; fileName: string };

const UNFINISHED_STATUSES: FullParseResultResponse["status"][] = [
  "waiting",
  "queued",
  "processing",
];

const PARSE_RESULT_NOT_FOUND_CODE = "PARSE_RESULT_NOT_FOUND";

function stateForLoadError(status: number | null, code: string | null): OriginalSubmissionState {
  const isParseResultNotFound = status === 404 && code === PARSE_RESULT_NOT_FOUND_CODE;
  return isParseResultNotFound ? { kind: "notAvailable" } : { kind: "error" };
}

/** The document a result was parsed from, or null when it has no document or file name. */
export function submissionDocumentOf(result: FullParseResultResponse): SubmissionDocument | null {
  const fileName = result.submissionContext?.originalFileName;
  if (!result.hasDocument || !fileName) return null;
  return { docParseResultId: result.id, fileName };
}

function stateForResult(result: FullParseResultResponse): OriginalSubmissionState {
  const document = submissionDocumentOf(result);
  if (result.status === "failed") return { kind: "failed", document };
  if (UNFINISHED_STATUSES.includes(result.status)) return { kind: "notFinished", document };
  if (result.status !== "parsed") return { kind: "notAvailable" };
  const parsedJson = narrowLegacyParsedJson(result.parsedJson);
  if (!parsedJson) return { kind: "notAvailable" };
  return { kind: "sectionsLegacy", result, parsedJson };
}

/** What the Original Submission tab shows, from the entry's parse link and the state of the fetch. */
export function getOriginalSubmissionState(input: {
  docParseResultId: string | null;
  load: OriginalSubmissionLoad;
}): OriginalSubmissionState {
  const { docParseResultId, load } = input;
  if (docParseResultId === null) return { kind: "notAvailable" };
  if (load.kind === "error") return stateForLoadError(load.status, load.code);
  if (load.kind !== "loaded") return { kind: "loading" };
  return stateForResult(load.result);
}
